import { AnyAction, Dispatch } from 'redux';
import store from '../Store';
import { openSnackbar } from '../ui/Actions';
import {
    UPDATE_USER,
    UPDATE_AUTH_TOKEN,
    UPDATE_FEATURES,
    UPDATE_LOCAL_PREFS,
    UPDATE_USER_REQUEST,
    UPDATE_USER_SUCCESS,
    UPDATE_USER_FAILURE,
    CLEAR_USER_ERRORS
} from './Types';
import { Features, IUserReducerState, Prefs } from './userTypes';
import axios, { Canceler } from 'axios';

const CancelToken = axios.CancelToken;
let cancelUpdateUserName: Canceler,
    cancelUpdateUserPreferences: Canceler,
    cancelUpdateUserPhone: Canceler,
    cancelUpdateUserEmail: Canceler,
    cancelUpdateUserPassword: Canceler,
    cancelUpdateUserAvatar: Canceler;

export const updateUser = (user: Partial<IUserReducerState>, prefs?: Prefs) => ({
    type: UPDATE_USER,
    user,
    prefs
});

export const updateUserRequest = () => ({
    type: UPDATE_USER_REQUEST
});

export const updateUserSuccess = () => ({
    type: UPDATE_USER_SUCCESS
});

export const updateUserFailure = (errors?: Array<{ [key: string]: string }>) => ({
    type: UPDATE_USER_FAILURE,
    errors
});

export const clearUserErrors = () => ({
    type: CLEAR_USER_ERRORS
});

export const updateUserNameAsync = (user: {
    firstName: string;
    lastName: string;
    authenticityToken: string;
}): any => {
    cancelUpdateUserName?.();
    const formData = new URLSearchParams();
    formData.append('user[first_name]', user.firstName);
    formData.append('user[last_name]', user.lastName);
    formData.append('commit', 'SAVE CHANGES');
    formData.append('authenticity_token', user.authenticityToken);
    return async (dispatch: Dispatch) => {
        dispatch(updateUserRequest());
        return await axios
            .patch('/app/user/profile/update_name', formData, {
                cancelToken: new CancelToken(c => {
                    cancelUpdateUserName = c;
                })
            })
            .then((response: any) => {
                dispatch(updateUserSuccess());
                dispatch(updateUser(user as IUserReducerState));
                dispatch(openSnackbar('success', response?.data?.message));
            })
            .catch((error: any) => {
                console.error(error);

                dispatch(updateUserFailure());
                dispatch(updateUser(store.getState().user));
            });
    };
};

export const updateUserPhoneAsync = (user: { phone: string; authenticityToken: string }): any => {
    cancelUpdateUserPhone?.();
    const formData = new URLSearchParams();
    formData.append('user[phone]', user.phone);
    formData.append('commit', 'SAVE CHANGES');
    formData.append('authenticity_token', user.authenticityToken);
    return async (dispatch: any) => {
        dispatch(updateUserRequest());
        return await axios
            .patch('/app/user/profile/update_phone', formData, {
                cancelToken: new CancelToken(c => {
                    cancelUpdateUserPhone = c;
                })
            })
            .then((response: any) => {
                dispatch(updateUserSuccess());
                dispatch(updateUser(user as IUserReducerState));
                dispatch(openSnackbar('success', response?.data?.message));
            })
            .catch(error => {
                console.error(error);
                dispatch(updateUserFailure());
                dispatch(updateUser(store.getState().user));
            });
    };
};

export const updateUserEmailAsync = (
    user: { email: string; authenticityToken: string },
    password: string,
    onClose: () => void
): any => {
    cancelUpdateUserEmail?.();
    const formData = new URLSearchParams();
    formData.append('user[current_password]', password);
    formData.append('user[email]', user.email);
    formData.append('commit', 'SAVE CHANGES');
    formData.append('authenticity_token', user.authenticityToken);
    return async (dispatch: any) => {
        dispatch(updateUserRequest());
        return await axios
            .patch('/app/user/profile/update_email', formData, {
                cancelToken: new CancelToken(c => {
                    cancelUpdateUserEmail = c;
                })
            })
            .then((response: any) => {
                dispatch(updateUserSuccess());
                dispatch(updateUser(store.getState().user));
                dispatch(openSnackbar('success', response?.data?.message));
                onClose();
            })
            .catch(error => {
                console.error(error);
                dispatch(updateUserFailure(error?.response?.data?.errors));
                dispatch(updateUser(store.getState().user));
            });
    };
};

export const updateUserPasswordAsync = (
    passwords: {
        currentPassword: string;
        newPassword: string;
        passwordConfirmation: string;
    },
    authenticityToken: string,
    onClose: () => void
): any => {
    cancelUpdateUserPassword?.();
    const formData = new URLSearchParams();
    formData.append('user[current_password]', passwords.currentPassword);
    formData.append('user[password]', passwords.newPassword);
    formData.append('user[password_confirmation]', passwords.passwordConfirmation);
    formData.append('commit', 'CHANGE PASSWORD');
    formData.append('authenticity_token', authenticityToken);
    return async (dispatch: any) => {
        dispatch(updateUserRequest());
        return await axios
            .patch('/app/user/account/update_password', formData, {
                cancelToken: new CancelToken(c => {
                    cancelUpdateUserPassword = c;
                })
            })
            .then((response: any) => {
                dispatch(updateUserSuccess());
                dispatch(openSnackbar('success', response?.data?.message));
                onClose();
                window.location.replace('/user/login');
            })
            .catch(error => {
                console.error(error);
                dispatch(updateUserFailure(error?.response?.data?.errors));
                dispatch(updateUser(store.getState().user));
            });
    };
};

export const updateUserAvatarAsync = (avatar: File | null): any => {
    cancelUpdateUserAvatar?.();
    const formData = new FormData();
    if (avatar) formData.append('avatar', avatar);
    return async (dispatch: any) => {
        dispatch(updateUserRequest());
        return await axios
            .post('/app/user/profile/update_avatar', avatar ? formData : undefined, {
                cancelToken: new CancelToken(c => {
                    cancelUpdateUserAvatar = c;
                })
            })
            .then((response: any) => {
                dispatch(updateUserSuccess());
                if (avatar) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        dispatch(updateUser({ avatar: reader.result }));
                    };
                    reader.readAsDataURL(avatar);
                } else {
                    dispatch(updateUser({ avatar: '' }));
                }
                dispatch(openSnackbar('success', response?.data?.message));
            })
            .catch(error => {
                console.error(error);
                dispatch(updateUserFailure(error?.response?.data?.errors));
                dispatch(openSnackbar('error', error?.response?.data?.message));
                dispatch(updateUser(store.getState().user));
            });
    };
};

export const updateUserPreferencesAsync = (
    user: IUserReducerState,
    clientPrefs: Prefs,
    prefs: Prefs
) => {
    cancelUpdateUserPreferences?.();
    return async (dispatch: any) => {
        dispatch(updateUserRequest());
        // dispatch(updateUser(user, clientPrefs));
        return await axios
            .post(
                '/app/user/account/update_preferences',
                {
                    preferences: prefs
                },
                {
                    cancelToken: new CancelToken(c => {
                        cancelUpdateUserPreferences = c;
                    })
                }
            )
            .then(response => {
                dispatch(updateUserSuccess());
                dispatch(updateUser(user, clientPrefs));
            })
            .catch(error => {
                console.error(error);
                dispatch(updateUserFailure());
            });
    };
};

export const updateAuthToken = (token: string) => ({
    type: UPDATE_AUTH_TOKEN,
    token
});
export const updateFeatures = (features: Features) => ({
    type: UPDATE_FEATURES,
    features
});
export const updateLocalPrefs = (prefs: Prefs) => ({
    type: UPDATE_LOCAL_PREFS,
    prefs
});
